import { moodColor, moodLabel, signedMean, threadDay } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type OwnLifeEvent } from '../lib/api.js';
import { formatTime } from '../lib/mood.js';
import { FALLBACK, NameEditor, todayLocal, whenLine } from '../components/DayLifeEvent.js';

/**
 * One life event, with everything the app knows about the stretch it covers.
 *
 * DELIBERATELY MAXIMAL, and not the finished shape. The first cut was spare —
 * six things were proposed by the study and cut because they read badly during
 * a bereavement — and he looked at it and said it felt thin. He is right that
 * the trade between rich and kind was made without him. So this shows the lot,
 * in labelled blocks, and the subtraction happens next with him looking at it.
 *
 * Everything here is stored. Nothing is generated for this page: sixty model
 * calls about somebody's worst summer would be a worse mistake than a thin page.
 *
 * The blocks, in the order they appear, so they can be named when cutting:
 *   HEADER      name or dates, how long, where it started
 *   BEFORE      the month before it began, against the stretch itself
 *   TOTALS      what the whole run held
 *   DAYS        per day: counts, mood, the stored read, and what he wrote
 */
export function LifeEventPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Span | null>(null);
  const [failed, setFailed] = useState(false);
  const navigate = useNavigate();
  /* Close, rename, remove — HERE, on the thread's own page (14 Oct 2026). They
     used to live only inside "How today's going", six rows down the fold, which
     is not where anyone looks for the thing they are reading about. */
  const [acting, setActing] = useState<null | 'closing' | 'naming'>(null);
  const [endedOn, setEndedOn] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [busy, setBusy] = useState(false);
  const reload = (): void => {
    if (!id) return;
    void api.lifeEventSpan(id).then(setData).catch(() => setFailed(true));
  };
  const back = (): void => { if (window.history.length > 1) navigate(-1); else navigate('/'); };

  useEffect(() => {
    if (!id) return;
    void api.lifeEventSpan(id).then(setData).catch(() => setFailed(true));
  }, [id]);

  if (failed) {
    return (
      <div className="stack">
        <button type="button" className="linkish" onClick={back}>‹ Back</button>
        <div className="banner banner-error">
          That did not load. It may have been removed, or the connection dropped.
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '40dvh' }}>
        <span className="spinner" />
      </div>
    );
  }

  const { event, items, days, before, spanDays } = data;
  const wrote = new Map<string, SpanItem[]>();
  for (const it of items) wrote.set(it.day, [...(wrote.get(it.day) ?? []), it]);

  // Newest first, like every other list in this app.
  const rows = [...days].reverse();
  const rated = days.filter((d) => d.meanMood !== null);
  const meanDuring = rated.length
    ? rated.reduce((a, d) => a + (d.meanMood ?? 0), 0) / rated.length
    : null;

  /* Shared with the fold and the day mark. The local copy this replaced had no
     one-day branch, so a wedding marked and closed on the day read "12 June to
     12 June". */
  const when = whenLine(event.startedOn, event.endedOn);

  const total = (k: keyof DayRow): number => days.reduce((a, d) => a + (Number(d[k]) || 0), 0);
  // Doses leave today out while it is still going, as the Medications screen
  // does: an evening dose sits in the denominator hours before it can be in the
  // numerator, so including it made this disagree with that screen every day.
  const today = new Date().toLocaleDateString('en-CA');
  const finished = (k: keyof DayRow): number =>
    days.filter((d) => d.day !== today).reduce((a, d) => a + (Number(d[k]) || 0), 0);

  return (
    <div className="stack">
      <button type="button" className="linkish" onClick={back}>‹ Back</button>

      {/* ---- HEADER ---------------------------------------------------- */}
      <header className="stack-sm stack">
        {/* The fallback, not the date. This page is the fifth place an unnamed
            event is rendered and the only one that substituted the date for a
            name — so tapping "Something big / Since 4 August" in the Log landed
            on a page titled "Since 4 August", with the words he tapped nowhere
            on it and the date promoted to the title. */}
        <h1>{event.label?.trim() || FALLBACK}</h1>
        {/* His words, 14 Oct 2026: "a thread is a life event, could be big or
            small, but something that's impactful — we need that context". */}
        <p className="faint" style={{ margin: 0 }}>
          A thread is a life event, big or small — something that has been shaping your days. Named by you, or marked as “Something big”; the app never names one.
        </p>
        <p className="muted">
          {event.kind === 'thread' ? 'A thread · ' : ''}
          {`${when} · `}
          {/* A DAY COUNTER, which the app forbids everywhere else — "day 12 is
              a tally of someone's grief". Here for him to look at and cut.
              On a thread it is HIS count, from the date he said it runs from
              (release 2 of 2.0), and it reads "day N" rather than "N days". */}
          {event.kind === 'thread' && event.countFrom
            ? (() => {
                const { days, before } = threadDay(event.countFrom, todayLocal());
                return before
                  ? `counts from ${formatDay(event.countFrom)}`
                  : `day ${days}`;
              })()
            : `${spanDays} ${spanDays === 1 ? 'day' : 'days'}`}
        </p>
        {/* HIS OWN SENTENCE, from the entry that raised it. Cut from the first
            version because it belongs to a day fold, not to a page title. */}
        {event.quote && <p className="reading">“{event.quote}”</p>}
        {event.voiceLogId && (
          <Link className="option" to={`/journal/${event.voiceLogId}`}>
            <strong>Where this started</strong>
            <span>The entry it was marked from</span>
          </Link>
        )}
              {/* The controls, on the thing itself. "Close this off" is about the
            marking, not the thing — the fold's wording, kept. */}
        {acting === 'naming' ? (
          <NameEditor event={event} onDone={() => { setActing(null); reload(); }} onCancel={() => setActing(null)} />
        ) : acting === 'closing' ? (
          <div className="stack-sm stack">
            <label className="field">
              <span>Close it off as of</span>
              <input className="input" type="date" value={endedOn} max={new Date().toLocaleDateString('en-CA')} onChange={(e) => setEndedOn(e.target.value)} />
            </label>
            <p className="faint" style={{ margin: 0 }}>Closing stops it here — it doesn’t delete anything.</p>
            <div className="row" style={{ gap: 'var(--space-2)' }}>
              <button type="button" className="btn btn-primary btn-compact" disabled={busy || !/^\d{4}-\d{2}-\d{2}$/.test(endedOn)} onClick={() => { setBusy(true); void api.updateLifeEvent(event.id, { status: 'ended', endedOn }).then(() => { setActing(null); reload(); }).finally(() => setBusy(false)); }}>Close this off</button>
              <button type="button" className="btn btn-ghost btn-compact" onClick={() => setActing(null)}>Keep it going</button>
            </div>
          </div>
        ) : (
          <div className="row life-event-acts" style={{ gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            {event.endedOn === null ? (
              <button type="button" className="linkish linkish-quiet" onClick={() => setActing('closing')}>Close this off</button>
            ) : (
              <button type="button" className="linkish linkish-quiet" disabled={busy} onClick={() => { setBusy(true); void api.updateLifeEvent(event.id, { status: 'active', endedOn: null }).then(reload).finally(() => setBusy(false)); }}>Reopen it</button>
            )}
            <button type="button" className="linkish linkish-quiet" onClick={() => setActing('naming')}>Change the name</button>
            <button type="button" className="linkish linkish-quiet" disabled={busy} onClick={() => { if (window.confirm('Remove this from the record? The days and entries it covered stay.')) { setBusy(true); void api.deleteLifeEvent(event.id).then(() => navigate('/')).finally(() => setBusy(false)); } }}>Remove</button>
          </div>
        )}
      </header>

      {/* ---- BEFORE ---------------------------------------------------- */}
      {before.meanMood !== null && meanDuring !== null && (
        <section className="card stack-sm stack">
          <h2 className="section-head">Against the month before</h2>
          <p className="muted" style={{ margin: 0 }}>
            The four weeks before this averaged {signed(before.meanMood)}. Since it began,{' '}
            {signed(meanDuring)}.
          </p>
        </section>
      )}

      {/* ---- TOTALS ---------------------------------------------------- */}
      <section className="card stack-sm stack">
        <h2 className="section-head">The whole stretch</h2>
        <dl className="sv-facts">
          <Fact label="Days" value={`${spanDays}, ${days.filter(hasAnything).length} with something on them`} />
          <Fact label="Check-ins" value={String(total('checkins'))} />
          <Fact label="Journal" value={String(total('entries'))} />
          <Fact label="Thought records" value={String(total('records'))} />
          {total('sos') > 0 && <Fact label="Help now" value={String(total('sos'))} />}
          {finished('medsScheduled') > 0 && (
            <Fact
              label="Doses"
              value={`${finished('medsTaken')} of ${finished('medsScheduled')}`}
            />
          )}
        </dl>
      </section>

      {/* ---- THE SHAPE OF IT ------------------------------------------- */}
      {/*
        Shut by default, and that is the whole of what makes it acceptable.
        
        A mood chart across a life event is the summary strip the study cut, and
        the argument against it stands: a newborn's first six weeks average
        Rough, so a picture of the span cannot tell a birth from a crisis and
        will imply one caused the other. Behind a fold he opens on purpose it is
        a different act — he already knows which this was, and he asked to see
        it.
        
        No trend line. Points only. A line through gaps invents days he did not
        rate, and a slope is a claim about direction that nobody made.
      */}
      {rated.length > 1 && (
        <details className="day-summary-fold">
          <summary>The shape of it</summary>
          <SpanChart days={days} />
        </details>
      )}

      {/* ---- DAYS ------------------------------------------------------ */}
      <div className="stack-sm stack">
        {rows.map((d) => {
          const mine = wrote.get(d.day) ?? [];
          const quiet = !hasAnything(d) && mine.length === 0;
          return (
            <section key={d.day} className="stack-sm stack">
              <div className="row-between">
                <h2 className="section-head" style={{ margin: 0 }}>
                  {formatDay(d.day)}
                </h2>
                {/* A MOOD WORD PER DAY — the summary strip, in words. Cut from
                    the first version because a column of these in date order
                    reads as a verdict on the stretch. */}
                {d.meanMood !== null && (
                  <span style={{ color: moodColor(Math.round(d.meanMood)) }}>
                    {moodLabel(Math.round(d.meanMood))}
                  </span>
                )}
              </div>

              {/* COUNTS — cut from the first version as "a ledger of what he
                  managed while it happened". */}
              {!quiet && <p className="faint">{tally(d)}</p>}

              {/* THE STORED READ — the model's sentence about that day. Cut
                  from the first version as narration he did not ask for. */}
              {d.read && <p className="muted">{d.read}</p>}

              {mine.map((it) => (
                <Link key={it.id} className="option" to={destination(it)}>
                  <strong>{truncate(it.text, 80)}</strong>
                  <span>
                    {kindLabel(it.kind)} · {formatTime(it.at)}
                  </span>
                </Link>
              ))}

              {/* A GAP ROW — cut from the first version as the app noticing
                  his silence. */}
              {quiet && <p className="faint">Nothing logged.</p>}
            </section>
          );
        })}
      </div>
    </div>
  );
}


/**
 * Mood across the stretch: one coloured dot per day he rated, and nothing else.
 *
 * This was briefly replaced by the chart Trends draws, which brought snapping
 * and a readout with it and everything else besides — a seven-day mean, week
 * separators, Help now ticks, missed-dose diamonds, a calendar. He wanted the
 * dots back. He was right: on Trends all that furniture is the point, and here
 * it is a second screen's worth of apparatus on a fold you open to glance at a
 * shape.
 *
 * So the dots return, and the two things worth borrowing come with them: a hit
 * target per day so a 3px dot never has to be hit, and a line of plain words
 * under the chart saying what the day you are on actually held.
 *
 * Still nothing joined up. A line through gaps invents days he did not rate,
 * and a slope is a claim about direction nobody made.
 */
function SpanChart({ days }: { days: DayRow[] }): JSX.Element {
  const [active, setActive] = useState<number | null>(null);

  const W = 320;
  const H = 132;
  const L = 24;
  const T = 8;
  const B = 20;
  const plotH = H - T - B;
  const n = Math.max(days.length - 1, 1);
  const step = (W - L - 6) / n;
  const x = (i: number): number => L + i * step;
  const y = (m: number): number => T + ((5 - m) / 10) * plotH;

  const rated = days.filter((d) => d.meanMood !== null).length;

  /* What a day held, in words. The same order the Log's tally uses. */
  const say = (i: number): string => {
    const d = days[i]!;
    const bits = [
      d.meanMood === null ? 'no rating' : signed(d.meanMood),
      d.checkins > 0 && `${d.checkins} check-in${d.checkins === 1 ? '' : 's'}`,
      d.sos > 0 && `${d.sos} Help now`,
      d.entries > 0 && `${d.entries} entr${d.entries === 1 ? 'y' : 'ies'}`,
      d.records > 0 && `${d.records} thought record${d.records === 1 ? '' : 's'}`,
    ].filter(Boolean) as string[];
    return `${shortDay(d.day)} — ${bits.join(' · ')}`;
  };

  const onKey = (e: React.KeyboardEvent): void => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const at = active ?? days.length - 1;
    setActive(Math.min(days.length - 1, Math.max(0, at + (e.key === 'ArrowRight' ? 1 : -1))));
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        tabIndex={0}
        onKeyDown={onKey}
        onMouseLeave={() => setActive(null)}
        aria-label={`Mood on the ${rated} days you rated. Arrow along to read one day.`}
        /* A viewBox does not size an svg inside a flex column — it collapsed to
           a smudge a few pixels tall with the dots stacked inside. */
        style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: `${W} / ${H}` }}
      >
        {[5, 0, -5].map((v) => (
          <text
            key={v}
            x={L - 6}
            y={y(v) + 3}
            textAnchor="end"
            fill="var(--text-faint)"
            style={{ fontSize: 9 }}
          >
            {v > 0 ? `+${v}` : v}
          </text>
        ))}

        {/* Only the middle is drawn: above or below it is the one comparison
            here that needs no explaining. */}
        <line x1={L} y1={y(0)} x2={W - 6} y2={y(0)} stroke="var(--border-strong)" strokeWidth="1" />

        {active !== null && (
          <line
            x1={x(active)}
            y1={T}
            x2={x(active)}
            y2={T + plotH}
            stroke="var(--border-strong)"
            strokeWidth="1"
          />
        )}

        {days.map((d, i) =>
          d.meanMood === null ? null : (
            <circle
              key={d.day}
              cx={x(i)}
              cy={y(d.meanMood)}
              r={active === i ? 4.5 : 3}
              fill={moodColor(Math.round(d.meanMood))}
            />
          ),
        )}

        <text x={L} y={H - 6} fill="var(--text-faint)" style={{ fontSize: 9 }}>
          {shortDay(days[0]!.day)}
        </text>
        <text x={W - 6} y={H - 6} textAnchor="end" fill="var(--text-faint)" style={{ fontSize: 9 }}>
          {shortDay(days[days.length - 1]!.day)}
        </text>

        {/* One target per day, spanning the plot, so a 3px dot never has to be
            hit — and so a day he did NOT rate can still be asked about. */}
        {days.map((d, i) => (
          <rect
            key={`hit-${d.day}`}
            x={x(i) - step / 2}
            y={T}
            width={Math.max(step, 6)}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setActive(i)}
            onClick={() => setActive(i)}
            style={{ cursor: 'pointer' }}
          />
        ))}
      </svg>

      <div className="chart-readout" aria-live="polite">
        {active === null ? (
          <>
            <p className="chart-readout-line">
              {rated} of {days.length} days rated.
            </p>
            <p className="chart-readout-hint">
              Tap, hover or arrow along the chart to read one day.
            </p>
          </>
        ) : (
          <p className="chart-readout-line">{say(active)}</p>
        )}
      </div>
    </div>
  );
}

function shortDay(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
}

function Fact({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function tally(d: DayRow): string {
  const bits = [
    d.checkins > 0 && `${d.checkins} check-in${d.checkins === 1 ? '' : 's'}`,
    d.sos > 0 && `${d.sos} Help now`,
    d.entries > 0 && `${d.entries} entr${d.entries === 1 ? 'y' : 'ies'}`,
    d.records > 0 && `${d.records} thought record${d.records === 1 ? '' : 's'}`,
    d.medsScheduled > 0 && `${d.medsTaken}/${d.medsScheduled} doses`,
  ].filter(Boolean) as string[];
  return bits.join(' · ');
}

function hasAnything(d: DayRow): boolean {
  return d.checkins + d.entries + d.records + d.sos + d.medsScheduled > 0;
}

/* One definition, in @adhd/shared — four copies disagreed. */
const signed = signedMean;

type DayRow = {
  day: string;
  checkins: number;
  entries: number;
  records: number;
  sos: number;
  medsTaken: number;
  medsScheduled: number;
  meanMood: number | null;
  read: string | null;
  state: string | null;
};
type SpanItem = {
  kind: 'journal' | 'record' | 'note';
  id: string;
  day: string;
  at: string;
  text: string;
};
type Span = {
  event: OwnLifeEvent;
  items: SpanItem[];
  days: DayRow[];
  before: { meanMood: number | null; days: number };
  spanDays: number;
};

function destination(it: SpanItem): string {
  if (it.kind === 'journal') return `/journal/${it.id}`;
  if (it.kind === 'record') return `/thought-record/${it.id}`;
  return `/history#day-${it.day}`;
}

function kindLabel(kind: SpanItem['kind']): string {
  return kind === 'journal' ? 'Journal' : kind === 'record' ? 'Thought record' : 'Check-in';
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function formatDay(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/*
 * The private copy of this count is gone. It clamped with `Math.max(1, …)`,
 * so a thread whose count runs from a future date read "day 1" here for the
 * eighteen days before that date arrived, while the chip on Today read
 * "from 1 Nov" about the same thread. One function in @adhd/shared now, and
 * it reports `before` rather than inventing a day one.
 */
